import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sidebar } from "./AdminDashboard";
import { toast } from "sonner";
import { Plus, Tag, Percent, DollarSign, Calendar, Users, ToggleLeft, ToggleRight, Loader2 } from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CouponManagement() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    discount_type: "percent",
    discount_value: 10,
    max_uses: null,
    expires_at: ""
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchCoupons();
    }
  }, [isAuthenticated]);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: getAdminAuthHeaders()
      });
      setCoupons(response.data.coupons);
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    try {
      await axios.post(`${API}/admin/coupon/create`, {
        ...newCoupon,
        expires_at: newCoupon.expires_at || null
      }, {
        headers: getAdminAuthHeaders()
      });
      toast.success("Coupon created successfully!");
      setShowCreateDialog(false);
      setNewCoupon({
        code: "",
        discount_type: "percent",
        discount_value: 10,
        max_uses: null,
        expires_at: ""
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    }
  };

  const toggleCoupon = async (couponId) => {
    try {
      await axios.put(`${API}/admin/coupon/${couponId}/toggle`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("Coupon status updated");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to update coupon");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="coupons-title">
              Coupon Management
            </h1>
            <p className="text-slate-600">Create and manage discount codes</p>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-bronze hover:bg-amber-600 text-white"
            data-testid="create-coupon-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Coupon
          </Button>
        </div>

        {/* Coupons Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : coupons.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No coupons created yet</p>
              <Button 
                onClick={() => setShowCreateDialog(true)}
                variant="outline"
                className="mt-4"
              >
                Create Your First Coupon
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((coupon, index) => (
              <Card key={coupon.id || index} className="card-hover" data-testid={`coupon-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <code className="text-xl font-bold text-midnight font-mono bg-slate-100 px-3 py-1 rounded">
                      {coupon.code}
                    </code>
                    <Badge className={coupon.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-600">
                      {coupon.discount_type === 'percent' ? (
                        <Percent className="w-4 h-4" />
                      ) : (
                        <DollarSign className="w-4 h-4" />
                      )}
                      <span>
                        {coupon.discount_type === 'percent' 
                          ? `${coupon.discount_value}% off`
                          : `$${coupon.discount_value} off`}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>
                        {coupon.current_uses} / {coupon.max_uses || '∞'} uses
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Expires: {formatDate(coupon.expires_at)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Button 
                      variant="ghost" 
                      className="w-full"
                      onClick={() => toggleCoupon(coupon.id)}
                      data-testid={`toggle-coupon-${index}`}
                    >
                      {coupon.is_active ? (
                        <>
                          <ToggleRight className="w-4 h-4 mr-2 text-emerald-600" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-4 h-4 mr-2" />
                          Activate
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Coupon Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">Create New Coupon</DialogTitle>
              <DialogDescription>
                Set up a discount code for your plugin
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="code">Coupon Code</Label>
                <Input 
                  id="code"
                  placeholder="SAVE20"
                  value={newCoupon.code}
                  onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                  data-testid="coupon-code-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <Select 
                    value={newCoupon.discount_type}
                    onValueChange={(value) => setNewCoupon({...newCoupon, discount_type: value})}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discount_value">
                    {newCoupon.discount_type === 'percent' ? 'Discount %' : 'Discount $'}
                  </Label>
                  <Input 
                    id="discount_value"
                    type="number"
                    min="0"
                    max={newCoupon.discount_type === 'percent' ? 100 : undefined}
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon({...newCoupon, discount_value: parseFloat(e.target.value) || 0})}
                    data-testid="discount-value-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_uses">Max Uses (optional)</Label>
                  <Input 
                    id="max_uses"
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={newCoupon.max_uses || ""}
                    onChange={(e) => setNewCoupon({...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                    data-testid="max-uses-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expires_at">Expiry Date (optional)</Label>
                  <Input 
                    id="expires_at"
                    type="date"
                    value={newCoupon.expires_at}
                    onChange={(e) => setNewCoupon({...newCoupon, expires_at: e.target.value})}
                    data-testid="expiry-date-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createCoupon}
                className="bg-bronze hover:bg-amber-600 text-white"
                data-testid="submit-coupon-btn"
              >
                Create Coupon
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
